import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  businessByCategory,
  Business,
  SubscriptionPlan,
} from 'services/@types';
import { getStats } from '../../../../redux/stats/statsSlice';
import { RootState } from '../../../../redux/store';
import BusinessService from 'services/business.api';
import UserService from 'services/user.api';
import EventService from 'services/event.api';
import InviteService from 'services/invite.api';
import SubscriptionPlanService from 'services/subscriptionPlan.api';
import { DashboardFilterValues } from '../components/DashboardFilters';

const DEFAULT_FILTERS: DashboardFilterValues = {
  subscriptionType: '',
  dateFrom: '',
  dateTo: '',
  category: '',
};

export default function useStats() {
  const dispatch = useDispatch<any>();
  const { loading, error, stats, businessTags } = useSelector(
    (state: RootState) => ({
      loading: state.stats.loading,
      error: state.stats.error,
      stats: state.stats.stats,
      businessTags: state.businessTag.businessTags,
    }),
  );

  const [allBusinesses, setAllBusinesses] = useState<Business[]>([]);
  const [allPlans, setAllPlans] = useState<SubscriptionPlan[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalInvites, setTotalInvites] = useState(0);
  const [recentUsers, setRecentUsers] = useState<any[]>([]);
  const [topEvents, setTopEvents] = useState<any[]>([]);
  const [filters, setFilters] =
    useState<DashboardFilterValues>(DEFAULT_FILTERS);

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  // Categories for filter dropdown
  const categories = useMemo(
    () => businessTags.map((t: any) => ({ id: t.id, name: t.name })),
    [businessTags],
  );

  // Apply filters to businesses
  const filteredBusinesses = useMemo(() => {
    return allBusinesses.filter((b) => {
      if (
        filters.subscriptionType &&
        b.subscription?.type !== filters.subscriptionType
      ) {
        return false;
      }
      if (filters.dateFrom && b.createdAt) {
        if (new Date(b.createdAt) < new Date(filters.dateFrom)) return false;
      }
      if (filters.dateTo && b.createdAt) {
        if (new Date(b.createdAt) > new Date(filters.dateTo + 'T23:59:59'))
          return false;
      }
      if (filters.category && !(b.category || []).includes(filters.category)) {
        return false;
      }
      return true;
    });
  }, [allBusinesses, filters]);

  // Compute metrics from filtered data
  const recentBusinesses = useMemo(
    () => filteredBusinesses.slice(0, 5),
    [filteredBusinesses],
  );

  const subscriptionBreakdown = useMemo(() => {
    const typeCount: Record<string, number> = {};
    filteredBusinesses.forEach((b) => {
      const type = b.subscription?.type || 'none';
      typeCount[type] = (typeCount[type] || 0) + 1;
    });
    return Object.entries(typeCount)
      .filter(([type]) => type !== 'none')
      .map(([type, count]) => ({ type, count }));
  }, [filteredBusinesses]);

  const payingBusinesses = useMemo(
    () =>
      filteredBusinesses.filter((b) => b.subscription?.type === 'active')
        .length,
    [filteredBusinesses],
  );

  const trialBusinesses = useMemo(
    () =>
      filteredBusinesses.filter((b) => b.subscription?.type === 'trial').length,
    [filteredBusinesses],
  );

  const expiredSubscriptions = useMemo(
    () =>
      filteredBusinesses.filter((b) =>
        ['expired', 'trial_expired', 'cancelled'].includes(
          b.subscription?.type || '',
        ),
      ).length,
    [filteredBusinesses],
  );

  const planDistribution = useMemo(() => {
    const planCount: Record<string, { name: string; count: number }> = {};
    (allPlans || []).forEach((plan) => {
      if (plan.id) planCount[plan.id] = { name: plan.name, count: 0 };
    });
    filteredBusinesses.forEach((b) => {
      const planId =
        typeof b.subscription?.subscriptionPlanId === 'string'
          ? b.subscription.subscriptionPlanId
          : (b.subscription?.subscriptionPlanId as any)?.id;
      if (planId && planCount[planId]) planCount[planId].count++;
    });
    return Object.values(planCount)
      .filter((p) => p.count > 0)
      .sort((a, b) => b.count - a.count)
      .map((p) => ({ planName: p.name, count: p.count }));
  }, [filteredBusinesses, allPlans]);

  const businessesByCategory = useMemo(() => {
    const businessesByCat = stats.businessesByCategory;
    const businesses: businessByCategory[] = [];
    businessesByCat.forEach((business: any) => {
      const tag = businessTags.find((tag: any) => tag.id === business._id);
      if (tag) {
        businesses.push({ ...business, name: tag.name });
      }
    });
    return businesses;
  }, [stats.businessesByCategory, businessTags]);

  useEffect(() => {
    const fetchAll = async () => {
      await dispatch(getStats());

      try {
        const [
          businessData,
          plansData,
          usersData,
          eventsData,
          invitesData,
          recentUsersData,
          topEventsData,
        ] = await Promise.all([
          BusinessService.getBusinesses({
            limit: 500,
            sortBy: 'createdAt:desc',
          }),
          SubscriptionPlanService.getSubscriptionPlans({ limit: 100 }),
          UserService.getUsers({ limit: 1 }),
          EventService.getEvents({ limit: 1 }),
          InviteService.getInvites({ limit: 1, onlyEmailAndType: true }),
          UserService.getUsers({ limit: 10, sortBy: 'createdAt:desc' }),
          EventService.getEvents({
            limit: 50,
            sortBy: 'numberOfGuests:desc',
          }),
        ]);

        setAllBusinesses(businessData?.results || []);
        setAllPlans(plansData?.results || []);
        setTotalUsers(usersData?.totalResults || 0);
        setTotalEvents(eventsData?.totalResults || 0);
        setTotalInvites(invitesData?.totalResults || 0);
        setRecentUsers((recentUsersData?.results as any[]) || []);
        setTopEvents((topEventsData?.results as any[]) || []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };

    fetchAll();
  }, [dispatch]);

  return {
    loading,
    error,
    stats,
    businessesByCategory,
    recentBusinesses,
    subscriptionBreakdown,
    planDistribution,
    totalUsers,
    totalEvents,
    totalInvites,
    payingBusinesses,
    trialBusinesses,
    expiredSubscriptions,
    filteredTotal: filteredBusinesses.length,
    filters,
    setFilters,
    resetFilters,
    categories,
    recentUsers,
    topEvents,
    allBusinesses,
  };
}
